
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import Container from '@/components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import {
  PaymentSlipBufferModel,
  PaymentSlipModel,
  SlipBufferPageParamModel, SlipBufferRequestIdListModel, SlipBufferSetPaymentDateModel, PaymentSlipTotalAmountModel
} from '@/model/paymentSlip/paymentSlipModel'
import { formatDate, formatThousands } from '@/utils'
import { createModel } from '@/utils/component'
import {
  getSlipBufferPageList, updateSlipBufferPaymentDate, cancelSlipBufferApplication, getSlipBufferTotalAmount
} from '@/apis/paymentSlip/paymentSlip'
import { ElMessage } from 'element-plus'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'PaymentSlipBufferPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container,
    BaseDialog
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const tableRef = ref()
    const selectedRows = ref<PaymentSlipBufferModel[]>()
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'payee', label: t('pay.payee'), component: 'el-input', type: 'text', model: '' },
      { prop: 'paymentDate', label: t('pay.paymentDate'), component: 'el-date-picker', type: 'date', model: '', suffix: '前' }
      // { prop: 'currency', label: t('pay.currency'), component: 'el-input', type: 'text', model: '' }
    ]
    const paymentBufferTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'companyBankName', label: t('pay.PaymentBank') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'bankName', label: t('pay.ReceivingBank') },
      { prop: 'amount', label: t('pay.PaymentAmount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'paymentDate', label: t('pay.paymentDate'), formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd') },
      { prop: 'purpose', label: t('pay.purposeView') }
    ]
    const paymentBufferTotalAmountStruct = [
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'companyBankName', label: t('pay.PaymentBank') },
      { prop: 'totalAmount', label: t('pay.totalAmountPayable'), formatter: (row: any) => formatThousands(row.totalAmount), align: 'right' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as SlipBufferPageParamModel,
      tableData: Array<PaymentSlipBufferModel>(),
      columnStruct: paymentBufferTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      dialogShowPaymentSlip: false,
      datePickerDialogVisible: false,
      selectedDate: null as Date | null,
      dialogCancelFormVisible: false,
      vitroPayDialogVisible: false,
      dialogFormVisible: false,
      totalAmountDialogVisible: false,
      totalAmountStruct: paymentBufferTotalAmountStruct,
      totalAmountData: Array<PaymentSlipTotalAmountModel>(),
      vitroData: null as PaymentSlipModel | null,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let paymentDate = null
        if (dataMap.searchFormModel.paymentDate) {
          paymentDate = dayjs(dataMap.searchFormModel.paymentDate).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
        }
        const params: SlipBufferPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          payee: dataMap.searchFormModel.payee,
          dueDate: null,
          paymentDate: paymentDate,
          currency: dataMap.searchFormModel.currency,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getSlipBufferPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async viewDetail(row: any) {
        switch (row.status) {
          case 9:
          case 0:
          case 1:
          case 2:
            await router.push({ path: `bufferDetail/${row.id}` })
            break
          case 3:
            await router.push({ path: `acceptanceDetail/${row.id}` })
            break
          default:
            break
        }
      },
      async handleShowTotalAmount() {
        const data = await getSlipBufferTotalAmount()
        if (data && data.data && data.data.length > 0) {
          dataMap.totalAmountData = data.data
        } else {
          dataMap.tableData = []
        }
        dataMap.totalAmountDialogVisible = true
      }
    })
    const handlePaymentOperation = () => {
      // dataMap.dialogShowPaymentSlip = true
    }
    const updatePaymentDateItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedDate = null
        dataMap.datePickerDialogVisible = true
      } else {
        ElMessage({
          message: t('pay.selectTip'),
          type: 'error'
        })
      }
    }
    const onDatePickerDialogConfirm = async() => {
      if (dataMap.selectedDate != null) {
        const params: SlipBufferSetPaymentDateModel = {
          idList: tableRef.value.getSelectionRows().map((row: any) => row.id),
          paymentDate: formatDate(dataMap.selectedDate)
        }
        const res = await updateSlipBufferPaymentDate(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.datePickerDialogVisible = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    const cancelPayItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.dialogCancelFormVisible = true
      } else {
        ElMessage({
          message: t('pay.selectTip'),
          type: 'error'
        })
      }
    }
    const handleCancelSelect = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        const params: SlipBufferRequestIdListModel = { idList: tableRef.value.getSelectionRows().map((row: any) => row.id) }
        const res = await cancelSlipBufferApplication(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.cancelSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        dataMap.dialogCancelFormVisible = false
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      } else {
        ElMessage({
          message: t('pay.selectTip'),
          type: 'error'
        })
      }
    }
    const tableRowClassName = ({ row }: { row: PaymentSlipBufferModel }) => {
      if (row.paymentDate !== null) {
        // 复制 Date 对象，避免修改原对象
        const paymentDate = new Date(row.paymentDate).setHours(0, 0, 0, 0)
        const nowDateTime = new Date().setHours(0, 0, 0, 0)
        if (paymentDate <= nowDateTime) {
          return 'warning-row'
        }
        return ''
      }
      return ''
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      tableRef,
      selectedRows,
      tableRowClassName,
      handlePaymentOperation,
      updatePaymentDateItemClick,
      onDatePickerDialogConfirm,
      cancelPayItemClick,
      handleCancelSelect,
      ...toRefs(dataMap)
    }
  }
})
